import {
  doc, getDoc, setDoc, updateDoc, deleteDoc, addDoc, collection, query,
  where, getDocs, serverTimestamp, limit, onSnapshot,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { toLatLng, toDate } from '../models';

export async function fetchUserProfile(uid) {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? { id: snap.id, uid: snap.id, ...snap.data() } : null;
}

export async function updateUserProfile(uid, fields) {
  await setDoc(doc(db, 'users', uid), { ...fields, updatedAt: serverTimestamp() }, { merge: true });
}

export async function uploadUserPhoto(uid, file) {
  const r = ref(storage, `users/${uid}/profile-${Date.now()}`);
  await uploadBytes(r, file);
  const url = await getDownloadURL(r);
  await updateUserProfile(uid, { profileImageURL: url, photoURL: url });
  return url;
}

// ---------- Restaurant ----------

export function parseRestaurant(id, d) {
  return {
    id,
    name: d.name || d.restaurantName || '',
    email: d.email || '',
    phone: d.phone || d.phoneNumber || '',
    address: d.address || '',
    parish: d.parish || '',
    island: d.island || '',
    imageURL: d.imageURL || d.imageUrl || d.bannerURL || '',
    bannerURL: d.bannerURL || d.imageURL || d.imageUrl || '',
    description: d.description || '',
    cuisine: d.cuisine || d.cuisineType || '',
    restaurantType: d.restaurantType || d.type || '',
    isAcceptingOrders: d.isAcceptingOrders !== false,
    isOpen: d.isOpen !== false,
    openingTime: d.openingTime || d.openTime || '',
    closingTime: d.closingTime || d.closeTime || '',
    operatingHours: d.operatingHours || null,
    coordinates: toLatLng(d.coordinates || d.location),
    ownerId: d.ownerId || d.userId || '',
    rating: d.rating || 0,
    createdAt: toDate(d.createdAt),
    raw: d,
  };
}

export async function fetchRestaurant(restaurantId) {
  if (!restaurantId) return null;
  const snap = await getDoc(doc(db, 'restaurants', restaurantId));
  return snap.exists() ? parseRestaurant(snap.id, snap.data()) : null;
}

// Fallback: find the restaurant owned by this user (by email or ownerId).
export async function fetchRestaurantForUser(user) {
  if (!user) return null;
  if (user.restaurantId) return fetchRestaurant(user.restaurantId);
  if (user.email) {
    const q = query(collection(db, 'restaurants'), where('email', '==', user.email), limit(1));
    const snap = await getDocs(q);
    if (!snap.empty) return parseRestaurant(snap.docs[0].id, snap.docs[0].data());
  }
  const q2 = query(collection(db, 'restaurants'), where('ownerId', '==', user.uid), limit(1));
  const snap2 = await getDocs(q2);
  return snap2.empty ? null : parseRestaurant(snap2.docs[0].id, snap2.docs[0].data());
}

export async function updateRestaurant(restaurantId, fields) {
  await setDoc(doc(db, 'restaurants', restaurantId), { ...fields, updatedAt: serverTimestamp() }, { merge: true });
}

// iOS toggles both restaurants/{id}.isAcceptingOrders and locations/{id}.isOpen
export async function setRestaurantAcceptingOrders(restaurantId, accepting) {
  await updateRestaurant(restaurantId, { isAcceptingOrders: accepting, isOpen: accepting });
  try {
    await setDoc(doc(db, 'locations', restaurantId), { isOpen: accepting, updatedAt: serverTimestamp() }, { merge: true });
  } catch (e) {
    console.warn('locations doc update failed:', e);
  }
}

export async function uploadRestaurantImage(restaurantId, file, kind = 'banner') {
  const r = ref(storage, `restaurants/${restaurantId}/${kind}-${Date.now()}`);
  await uploadBytes(r, file);
  const url = await getDownloadURL(r);
  await updateRestaurant(restaurantId, kind === 'banner'
    ? { bannerURL: url, imageURL: url }
    : { [`${kind}URL`]: url });
  return url;
}

// ---------- Menu items ----------
// iOS stores menu items in restaurants/{id}/menuItems with field
// `menuItemImageURL` — same path the customer web app reads.

const menuItemsCol = (restaurantId) => collection(db, 'restaurants', restaurantId, 'menuItems');

export function parseMenuItem(id, d) {
  const imageURL = d.menuItemImageURL || d.imageURL || d.imageUrl || d.image || '';
  return {
    id,
    restaurantId: d.restaurantId || '',
    name: d.name || '',
    description: d.description || '',
    price: d.price || 0,
    category: d.category || 'Other',
    imageURL,
    menuItemImageURL: imageURL,
    isAvailable: d.isAvailable !== false,
    preparationTime: d.preparationTime || 0,
    calories: d.calories ?? null,
    allergens: d.allergens || [],
    dietaryRestrictions: d.dietaryRestrictions || [],
    availablePeriods: d.availablePeriods || [],
    isFeatured: !!d.isFeatured,
    isFavorite: !!d.isFavorite,
    // Optional stock tracking — retail-style inventory. When trackStock is
    // on, stockCount decrements per purchase and 0 = out of stock.
    trackStock: d.trackStock === true,
    stockCount: Number.isFinite(d.stockCount) ? d.stockCount : 0,
    // Size/variant options (retail items): [{id, name, stock?}] — stock null/undefined = unlimited
    variants: Array.isArray(d.variants)
      ? d.variants
          .filter(v => v && typeof v.name === 'string' && v.name.trim())
          .map(v => ({ id: v.id || Math.random().toString(36).slice(2), name: v.name.trim(), stock: Number.isFinite(v.stock) ? v.stock : null }))
      : [],
    createdAt: toDate(d.createdAt),
    raw: d,
  };
}

export async function fetchMenuItems(restaurantId) {
  const snap = await getDocs(menuItemsCol(restaurantId));
  return snap.docs.map(d => parseMenuItem(d.id, d.data()));
}

export function listenToMenuItems(restaurantId, cb, onErr) {
  return onSnapshot(menuItemsCol(restaurantId),
    snap => cb(snap.docs.map(d => parseMenuItem(d.id, d.data()))),
    e => { console.error('listenToMenuItems', e); onErr && onErr(e); });
}

export async function saveMenuItem(item) {
  const data = {
    restaurantId: item.restaurantId,
    name: item.name,
    description: item.description || '',
    price: Number(item.price) || 0,
    category: item.category || 'Other',
    menuItemImageURL: item.menuItemImageURL || item.imageURL || '',
    isAvailable: item.isAvailable !== false,
    preparationTime: Number(item.preparationTime) || 0,
    calories: item.calories ?? null,
    allergens: item.allergens || [],
    dietaryRestrictions: item.dietaryRestrictions || [],
    availablePeriods: item.availablePeriods || [],
    isFeatured: !!item.isFeatured,
    isFavorite: !!item.isFavorite,
    trackStock: item.trackStock === true,
    stockCount: item.trackStock === true ? Math.max(0, Math.floor(Number(item.stockCount) || 0)) : 0,
    variants: Array.isArray(item.variants)
      ? item.variants
          .filter(v => v && typeof v.name === 'string' && v.name.trim())
          .map(v => {
            const out = { id: v.id || Math.random().toString(36).slice(2), name: v.name.trim() };
            if (Number.isFinite(v.stock)) out.stock = Math.max(0, Math.floor(v.stock));
            return out;
          })
      : [],
    updatedAt: serverTimestamp(),
  };
  if (item.id) {
    await setDoc(doc(db, 'restaurants', item.restaurantId, 'menuItems', item.id), data, { merge: true });
    return item.id;
  }
  const ref = await addDoc(menuItemsCol(item.restaurantId), { ...data, createdAt: serverTimestamp() });
  return ref.id;
}

export async function deleteMenuItem(restaurantId, itemId) {
  await deleteDoc(doc(db, 'restaurants', restaurantId, 'menuItems', itemId));
}

export async function setMenuItemAvailability(restaurantId, itemId, isAvailable) {
  await updateDoc(doc(db, 'restaurants', restaurantId, 'menuItems', itemId), { isAvailable, updatedAt: serverTimestamp() });
}

// Storage rules allow writes under menu-items/{restaurantId}/{imageName}
// (3 segments) — the flat 2-segment path iOS uses matches no rule.
export async function uploadMenuItemImage(restaurantId, itemId, file) {
  const r = ref(storage, `menu-items/${restaurantId}/${itemId || 'new'}_${Date.now()}.jpg`);
  await uploadBytes(r, file);
  return getDownloadURL(r);
}

// ---------- Driver ----------

export function parseDriverDoc(id, d) {
  return {
    id,
    userId: d.userId || id,
    name: d.name || d.fullName || '',
    email: d.email || '',
    phone: d.phone || d.phoneNumber || '',
    isOnline: !!d.isOnline,
    isAvailable: !!d.isAvailable,
    status: d.status || 'offline',
    vehicleType: d.vehicleType || '',
    vehicleNumber: d.vehicleNumber || d.vehiclePlate || '',
    licenseNumber: d.licenseNumber || d.license || '',
    totalEarnings: d.totalEarnings || 0,
    totalDeliveries: d.totalDeliveries || 0,
    rating: d.rating || d.averageRating || 0,
    onTimeRate: d.onTimeRate || 0,
    averageDeliveryTime: d.averageDeliveryTime || 0,
    profileImageURL: d.profileImageURL || d.photoURL || '',
    currentLocation: toLatLng(d.currentLocation || d.location),
    createdAt: toDate(d.createdAt),
    raw: d,
  };
}

export async function fetchDriver(driverId) {
  const snap = await getDoc(doc(db, 'drivers', driverId));
  return snap.exists() ? parseDriverDoc(snap.id, snap.data()) : null;
}

export function listenToDriver(driverId, cb, onErr) {
  return onSnapshot(doc(db, 'drivers', driverId),
    s => cb(s.exists() ? parseDriverDoc(s.id, s.data()) : null),
    e => { console.error('listenToDriver', e); onErr && onErr(e); });
}

export async function updateDriver(driverId, fields) {
  await setDoc(doc(db, 'drivers', driverId), { ...fields, updatedAt: serverTimestamp() }, { merge: true });
}

// Online/offline toggle — writes user.isOnline + driver fields (iOS contract).
export async function setDriverOnline(driverId, uid, online) {
  await updateUserProfile(uid, { isOnline: online });
  await updateDriver(driverId, {
    isOnline: online,
    isAvailable: online,
    status: online ? 'online' : 'offline',
  });
}

// Write current position to drivers/{id}/locations/current (customer tracking).
export async function writeDriverLocation(driverId, coords, orderId = null) {
  await setDoc(doc(db, 'drivers', driverId, 'locations', 'current'), {
    latitude: coords.lat,
    longitude: coords.lng,
    timestamp: serverTimestamp(),
    orderId: orderId || null,
    isOnline: true,
  }, { merge: true });
  await updateDriver(driverId, {
    currentLocation: { latitude: coords.lat, longitude: coords.lng },
    lastLocationUpdate: serverTimestamp(),
  });
}
